'use client'

import { BorrowMultiplyTabs } from '@/features/eborrow-details/components/BorrowMultiplyTabs'
import { useBorrowDetails } from '@/features/eborrow-details/logic/useBorrowDetails'
import { Skeleton } from '@/ui/atoms/skeleton/Skeleton'
import { Typography } from '@/ui/atoms/typography/Typography'
import { Address } from 'viem'

import type { RouteType } from './MarketExplorer'

interface PositionComposerProps {
  collateral: Address
  liability: Address
  collateralSymbol?: string
  liabilitySymbol?: string
  onLtvChange?: (ltv: number) => void
  /** When provided, use this subAccountId for operations (modifying existing position) */
  selectedSubAccountId?: number
  /** Existing position collateral value in USD */
  existingCollateralUsd?: number
  /** Existing position debt value in USD */
  existingDebtUsd?: number
  /** Controlled collateral amount */
  collateralAmount?: string
  /** Collateral amount change handler */
  onCollateralAmountChange?: (value: string) => void
  /** Controlled borrow amount */
  borrowAmount?: string
  /** Borrow amount change handler */
  onBorrowAmountChange?: (value: string) => void
  /** Existing controller to disable before enabling new one (for controller switching) */
  existingController?: Address
  /** Whether to use native HYPE for collateral (wraps on deposit) */
  useNativeCollateral?: boolean
  /** Whether to receive native HYPE for borrow (unwraps on receive) */
  receiveNativeBorrow?: boolean
  /** Controlled active tab */
  activeTab?: 'Borrow' | 'Multiply'
  /** Controlled active tab change handler */
  onActiveTabChange?: (tab: 'Borrow' | 'Multiply') => void
  /** If true, show tabs even in controlled mode (allows toggling strategy) */
  showTabsWhenControlled?: boolean
  /** Route type: 'euler' for Mewler/EVC routes, 'pooled' for HypurrFi Pooled */
  routeType?: RouteType
}

export function PositionComposer({
  collateral,
  liability,
  onLtvChange,
  selectedSubAccountId,
  existingCollateralUsd,
  existingDebtUsd,
  collateralAmount,
  onCollateralAmountChange,
  borrowAmount,
  onBorrowAmountChange,
  existingController,
  useNativeCollateral,
  receiveNativeBorrow,
  activeTab,
  onActiveTabChange,
  showTabsWhenControlled,
  routeType = 'euler',
}: PositionComposerProps) {
  const { data, isLoading } = useBorrowDetails({ collateral, liability, subAccountId: selectedSubAccountId })

  const handleLtvChange = (ltv: number) => {
    onLtvChange?.(ltv)
  }

  if (isLoading) {
    return (
      <div className="flex h-full flex-col gap-4">
        <Skeleton className="mb-4 h-12 w-1/3" />
        <Skeleton className="h-full w-full rounded-2xl" />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex h-full items-center justify-center rounded-2xl border border-white/10 bg-white/[.02] p-8">
        <Typography variant="p" className="text-muted-foreground">
          Unable to load market details.
        </Typography>
      </div>
    )
  }

  // Use effective max/liquidation LTV from Account Lens when available (for multi-collateral positions)
  // Fall back to pair-specific LTV when Account Lens data isn't available
  const effectiveMaxLTV = data.userPosition?.accountEffectiveMaxLTV
  const effectiveLiquidationLTV = data.userPosition?.accountEffectiveLiquidationLTV
  const maxLTVToUse = effectiveMaxLTV !== undefined ? BigInt(Math.round(effectiveMaxLTV * 100)) : data.maxLTV
  const liquidationLTVToUse =
    effectiveLiquidationLTV !== undefined ? BigInt(Math.round(effectiveLiquidationLTV * 100)) : data.liquidationLTV

  return (
    <BorrowMultiplyTabs
      collateral={data.collateral}
      liability={data.liability}
      collateralSymbol={data.collateralSymbol}
      liabilitySymbol={data.liabilitySymbol}
      maxLTV={maxLTVToUse}
      liquidationLTV={liquidationLTVToUse}
      borrowAPY={data.borrowAPY}
      supplyAPY={data.supplyAPY}
      collateralIntrinsicAPY={data.collateralStats?.intrinsicAPY}
      collateralLendingAPY={data.collateralStats?.lendingAPY}
      evcAddress={data.evcAddress}
      collateralPriceUSD={data.oraclePrice || 1}
      liabilityPriceUSD={data.liabilityStats?.priceUSD || 1}
      onLtvChange={handleLtvChange}
      totalBorrowed={data.liabilityStats?.totalBorrowsNormalized}
      borrowCap={data.liabilityStats?.borrowCap}
      availableLiquidity={data.liabilityStats?.availableLiquidityNormalized}
      collateralMaxDeposit={data.collateralStats?.maxDeposit}
      selectedSubAccountId={selectedSubAccountId}
      existingCollateralUsd={existingCollateralUsd}
      existingDebtUsd={existingDebtUsd}
      controlledCollateralAmount={collateralAmount}
      onControlledCollateralAmountChange={onCollateralAmountChange}
      controlledBorrowAmount={borrowAmount}
      onControlledBorrowAmountChange={onBorrowAmountChange}
      existingController={existingController}
      controlledUseNativeCollateral={useNativeCollateral}
      controlledReceiveNativeBorrow={receiveNativeBorrow}
      controlledActiveTab={activeTab}
      onControlledActiveTabChange={onActiveTabChange}
      showTabsWhenControlled={showTabsWhenControlled}
      routeType={routeType}
    />
  )
}
